import React, { useEffect, useState } from "react";
import { useUpdatePasswordMutation } from "../usersApiSlice";

// 8–12 chars (matches the mockup copy and the backend minimum); letters/digits/!@#$%.
const PWD_REGEX = /^[A-Za-z0-9!@#$%]{8,12}$/;

const labelClass =
  "mb-[7px] block text-[12px] font-bold tracking-[0.04em] text-muted";

/** Password panel — real updateMyPassword mutation, preserved validation. */
const UpdatePassword = () => {
  const [updatePassword, { isLoading, isSuccess }] =
    useUpdatePasswordMutation();

  const [passwordCurrent, setPasswordCurrent] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [errMsg, setErrMsg] = useState("");
  const [successShown, setSuccessShown] = useState(false);

  useEffect(() => {
    if (isSuccess) {
      setPasswordCurrent("");
      setPassword("");
      setPasswordConfirm("");
      setErrMsg("");
      setSuccessShown(true);
    }
  }, [isSuccess]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!PWD_REGEX.test(passwordCurrent) || !PWD_REGEX.test(password)) {
      setErrMsg("Passwords must be 8–12 characters.");
      return;
    }
    if (password !== passwordConfirm) {
      setErrMsg("New passwords don't match.");
      return;
    }
    setErrMsg("");
    setSuccessShown(false);
    try {
      await updatePassword({
        passwordCurrent,
        password,
        passwordConfirm,
      }).unwrap();
    } catch {
      setErrMsg("Couldn't update your password. Check your current password.");
    }
  };

  return (
    <div className="max-w-[520px] rounded-[20px] bg-white p-[30px] shadow-[0_14px_30px_-22px_rgba(0,0,0,0.25)]">
      <h2 className="mb-1 font-serif text-[26px] font-normal text-ink">
        Change password
      </h2>
      <p className="mb-[26px] text-[14px] text-muted-300">
        Choose something 8–12 characters long.
      </p>

      <form onSubmit={handleSubmit}>
        <div className="mb-[18px]">
          <label htmlFor="pwd-current" className={labelClass}>
            CURRENT PASSWORD
          </label>
          <input
            id="pwd-current"
            type="password"
            className="wn-field"
            placeholder="••••••••"
            value={passwordCurrent}
            onChange={(e) => setPasswordCurrent(e.target.value)}
            required
          />
        </div>
        <div className="mb-[18px]">
          <label htmlFor="pwd-new" className={labelClass}>
            NEW PASSWORD
          </label>
          <input
            id="pwd-new"
            type="password"
            className="wn-field"
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </div>
        <div className="mb-[26px]">
          <label htmlFor="pwd-confirm" className={labelClass}>
            CONFIRM NEW PASSWORD
          </label>
          <input
            id="pwd-confirm"
            type="password"
            className="wn-field"
            placeholder="••••••••"
            value={passwordConfirm}
            onChange={(e) => setPasswordConfirm(e.target.value)}
            required
          />
        </div>

        {successShown && (
          <div className="mb-4 rounded-[11px] bg-easy/10 px-3.5 py-3 text-[13.5px] font-semibold text-easy">
            Password updated successfully!
          </div>
        )}
        {errMsg && (
          <div className="mb-4 rounded-[11px] bg-hard/10 px-3.5 py-3 text-[13.5px] font-semibold text-hard">
            {errMsg}
          </div>
        )}

        <button
          type="submit"
          disabled={isLoading}
          className="rounded-xl bg-secondary-400 px-[26px] py-3 text-[15px] font-bold text-white transition-colors hover:bg-ink disabled:opacity-50"
        >
          {isLoading ? "Updating…" : "Update password"}
        </button>
      </form>
    </div>
  );
};

export default UpdatePassword;
